"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateSyncReportingInputConfig = void 0;
const athena_utilities_1 = require("@finley-technologies/athena-utilities");
const finley_date_1 = require("@finley-technologies/finley-date");
const utils_1 = require("../utils");
const dataAccessorValidate_1 = require("./dataAccessorValidate");
const intermediateFieldsValidate_1 = require("./intermediateFieldsValidate");
const _attachSupplementVariables = (params) => {
    const { athenaQueries, athenaRecurringQueries } = params.config;
    const updatedAthenaQueries = athenaQueries.map((query) => ({
        ...query,
        variables: [...utils_1.SUPPLEMENTAL_QUERY_VARIABLES, ...query.variables],
    }));
    const updatedAthenaRecurringQueries = athenaRecurringQueries.map((query) => ({
        ...query,
        variables: [...utils_1.SUPPLEMENTAL_QUERY_VARIABLES, ...query.variables],
    }));
    // NOTE: "dataAccessors" already add SUPPLEMENTAL_VARIABLES in its own validation,
    // so no need to attach them during this process.
    // Athena Queries require them since they're utilized outside "Reporting" as well,
    // so need to custom manage these accordingly
    return {
        ...params.config,
        athenaQueries: updatedAthenaQueries,
        athenaRecurringQueries: updatedAthenaRecurringQueries,
    };
};
const _checkDuplicateNames = (params) => {
    const { globalRecurringDateConfigs, athenaQueries, athenaRecurringQueries, dataAccessors, intermediateFields, } = params.config;
    const duplicateNames = [];
    const seenNames = new Set();
    for (const { name } of utils_1.SUPPLEMENTAL_QUERY_VARIABLES) {
        if (seenNames.has(name)) {
            duplicateNames.push(`Duplicate input name [type:supplemental] [name:${name}]`);
        }
        else {
            seenNames.add(name);
        }
    }
    for (const { field } of globalRecurringDateConfigs) {
        if (seenNames.has(field)) {
            duplicateNames.push(`Duplicate input name [type:globalRecurringDate] [name:${field}]`);
        }
        else {
            seenNames.add(field);
        }
    }
    for (const { query } of athenaQueries) {
        const { name } = query;
        if (seenNames.has(name)) {
            duplicateNames.push(`Duplicate input name [type:athenaQuery] [name:${name}]`);
        }
        else {
            seenNames.add(name);
        }
    }
    for (const { name } of athenaRecurringQueries) {
        if (seenNames.has(name)) {
            duplicateNames.push(`Duplicate input name [type:athenaRecurringQuery] [name:${name}]`);
        }
        else {
            seenNames.add(name);
        }
    }
    for (const { name } of dataAccessors) {
        if (seenNames.has(name)) {
            duplicateNames.push(`Duplicate input name [type:dataAccessor] [name:${name}]`);
        }
        else {
            seenNames.add(name);
        }
    }
    for (const { field } of intermediateFields) {
        if (seenNames.has(field)) {
            duplicateNames.push(`Duplicate input name [type:intermediateField] [name:${field}]`);
        }
        else {
            seenNames.add(field);
        }
    }
    return duplicateNames;
};
/**
 * This validates full Reporting inputs config. Note that this does this in a "sync",
 * which means it's not actually triggering the async data accessors and wouldn't know
 * the available field names for them to pass along.
 * Async input config validation incurs in Inputs evaluation step when data accessors
 * are actually triggered. This helps better ensure set up is done properly
 */
const validateSyncReportingInputConfig = (params) => {
    const duplicateNames = _checkDuplicateNames(params);
    // Return early if already know duplicate names to not overcrowd errors as subsequent validations
    // also independently checks each section for duplicates (i.e., there'd be a lot of overlap)
    if (duplicateNames.length > 0) {
        return duplicateNames;
    }
    const { globalRecurringDateConfigs, athenaQueries, athenaRecurringQueries, dataAccessors, intermediateFields, } = _attachSupplementVariables(params);
    const errors = [];
    // *** Global Recurring Date Validations ***
    const { errors: globalRecurringDateErrors, } = finley_date_1.recurringDate.validateGlobalRecurringDateConfigs({
        globalRecurringDateConfigs,
    });
    globalRecurringDateErrors.forEach((error) => errors.push(error));
    // *** Athena Query Validations ***
    const { errors: athenaQueryErrors } = athena_utilities_1.validate.validateAthenaQueries(athenaQueries);
    athenaQueryErrors.forEach((error) => errors.push(error));
    // *** Athena Recurring Query Validations ***
    const { errors: athenaRecurringQueryErrors, } = athena_utilities_1.validate.validateAthenaRecurringQueries({
        athenaRecurringQueries,
        globalRecurringDateConfigs,
    });
    athenaRecurringQueryErrors.forEach((error) => errors.push(error));
    // *** Data Accessor Validations ***
    const dataAccessorErrors = (0, dataAccessorValidate_1.validateDataAccessors)({ dataAccessors });
    dataAccessorErrors.forEach((error) => errors.push(error));
    // *** Intermediate Fields Validations ***
    const intermediateFieldErrors = (0, intermediateFieldsValidate_1.validateIntermediateFields)({
        intermediateFields,
        queryResults: [],
    });
    intermediateFieldErrors.forEach((error) => errors.push(error));
    return errors;
};
exports.validateSyncReportingInputConfig = validateSyncReportingInputConfig;
//# sourceMappingURL=inputConfigValidate.js.map